import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import SeoTagsManager from '../../utils/SeoTagsManager';
import { headingLetterSpacing } from '../../utils/headingTypography';

import ProviderTileComponentSummary from '../../components/ProviderTile/ProviderTileComponentSummary';
import ContactWindowRequest from '../../json/components/PopupModal/PopupModalData-input-contact-window-request.json';
import TakeMeHome from '../../json/components/PopupModal/PopupModalData-take-me-home.json';
import PopupModalComponent from '../../components/PopupModal/PopupModalComponent';
import { toSentenceCase } from '../../utils/casing';

import {
  deriveCallBackTime,
  buildContactWindowData,
} from '../../utils/apiHelpers';
import { useMobileContext } from '../../contexts/MobileContext';
import { requestEmployeeBenefitsContact } from '../../api/apiServices';
import { pushThankYouView } from '../../utils/dataLayer';

function StandaloneCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const quoteData = location.state;
  quoteData.LogoURL = 'https://assets.askhippo.co.za/Pages/Common/logo-nav.png';
  quoteData.FriendlyName = 'Hippo Advisory Services';

  const sessionId =
    localStorage.getItem('sessionId') || sessionStorage.getItem('sessionId');

  const { isMobile } = useMobileContext();

  const [showModal, setShowModal] = useState(false); // Schedule Call modal
  const [showTakeMeHome, setShowTakeMeHome] = useState(false); // Follow-up modal

  const staggerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const root = staggerRef.current;
      if (!root) return;

      // Immediately hide to prevent flash
      const all = root.querySelectorAll('.stagger-in');
      if (all.length) gsap.set(all, { opacity: 0, y: 30 });

      // Run slightly after DOM settles
      setTimeout(() => {
        const tl = gsap.timeline({
          defaults: { duration: 0.6, ease: 'power3.out' },
        });

        // 1) Title
        tl.to('.stagger-title', { opacity: 1, y: 0 })

          // 2) Provider tile (short gap)
          .to('.stagger-provider', { opacity: 1, y: 0 }, '+=0.1')

          // 3) Wait 3s, then bring in the rest in order:
          .to('.stagger-desc', { opacity: 1, y: 0 }, '+=1.3')
          .to('.stagger-btn-primary', { opacity: 1, y: 0 }, '+=0.08')
          .to('.stagger-btn-secondary', { opacity: 1, y: 0 }, '+=0.06');
      }, 30);
    }, staggerRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (quoteData.product === 'EmployeeBenefits') {
      pushThankYouView({
        product: quoteData.product,
        partner_name: quoteData.FriendlyName,
        formValues: quoteData.formValues,
      });
    }
  }, [quoteData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep initial acceptQuote call (no animation/confetti)
  useEffect(() => {
    const init = async () => {
      try {
        if (quoteData.product === 'EmployeeBenefits') {
          await requestEmployeeBenefitsContact(quoteData.formValues, sessionId);
        }
      } catch (err) {
        // ignore
      }
    };
    init();
  }, [sessionId]);

  const handleScheduleClick = () => setShowModal(true);

  const handleModalComplete = async (formValues) => {
    try {
      const timeRange =
        formValues?.contact_time_today || formValues?.contact_time_tomorrow;
      const callBackTime = deriveCallBackTime(
        formValues?.contact_window,
        timeRange
      );
      quoteData.formValues.callBackTime = callBackTime;

      const response = await requestEmployeeBenefitsContact(
        quoteData.formValues,
        sessionId
      );
      // Close first modal regardless; then branch on success
      setShowModal(false);
      if (response.success) {
        // Show follow-up modal
        setShowTakeMeHome(true);
      } else {
        alert('Failed to accept quote.');
      }
    } catch (err) {
      setShowModal(false);
    }
  };

  // When Take Me Home modal completes, navigate home
  const handleTakeMeHomeComplete = () => {
    setShowTakeMeHome(false);
    navigate('/');
  };

  const { FriendlyName } = quoteData || {};
  const contactWindowData = buildContactWindowData(
    ContactWindowRequest,
    FriendlyName
  );
  const { pathname } = useLocation();

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Container>
        <Box
          ref={staggerRef}
          sx={{
            pt: isMobile ? '37px' : '32px',
            width: isMobile ? '343px' : '448px',
            mx: 'auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Avatar is NOT part of the stagger sequence */}
          <img
            src={HippoAvatar}
            alt="Hippo Hand"
            style={{ width: '124px', height: '124px', objectFit: 'contain' }}
          />

          {/* Title */}
          <Typography
            variant="h5"
            className="stagger-in stagger-title"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              width: '100%',
              fontSize: isMobile ? '2rem' : '3rem',
              lineHeight: isMobile ? '34px' : '52px',
              letterSpacing: headingLetterSpacing({
                xs: '2rem',
                md: '3rem',
              }),
              color: '#162F3B',
            }}
          >
            Great, Thank You!
          </Typography>

          {/* ProviderTileComponentSummary */}
          <Box
            className="stagger-in stagger-provider"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: '32px',
              mb: '32px',
            }}
          >
            <ProviderTileComponentSummary data={quoteData} />
          </Box>

          {/* Description */}
          <Box
            className="stagger-in stagger-desc"
            sx={{ mt: '16px', width: '100%' }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                lineHeight: isMobile ? '24px' : '28px',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                maxWidth: '100%',
              }}
            >
              We’ve sent your details to {FriendlyName}. They’ll call to confirm
              your details and requirements.
            </Typography>
          </Box>

          {/* Primary Button */}
          <Box
            className="stagger-in stagger-btn-primary"
            sx={{ mt: isMobile ? '32px' : '58px' }}
          >
            <Button
              variant="contained"
              onClick={handleScheduleClick}
              sx={{
                width: '175px',
                height: '48px',
                gap: '12px',
                borderRadius: '20px',
                backgroundColor: '#FF0074',
                textTransform: 'none',
                fontFamily: 'Inter',
                fontWeight: 700,
                color: '#FFFFFF',
                fontSize: '1.25rem',
                lineHeight: '24px',
                letterSpacing: '-0.3px',
                '&:hover': { backgroundColor: '#e6006a' },
              }}
            >
              {toSentenceCase('Schedule Call')}
            </Button>
          </Box>

          {/* Secondary Button */}
          <Box
            className="stagger-in stagger-btn-secondary"
            sx={{ mt: isMobile ? '16px' : '22px' }}
          >
            <Button
              onClick={() => navigate('/')}
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                letterSpacing: '-0.002em',
                verticalAlign: 'middle',
                color: '#A2ACB1',
                cursor: 'pointer',
                px: '8px',
                py: '4px',
                borderRadius: '8px',
                textDecoration: 'none',
                textTransform: 'none',
              }}
            >
              {toSentenceCase('Take Me Home')}
            </Button>
          </Box>
        </Box>

        {/* Schedule Call modal */}
        {showModal && (
          <PopupModalComponent
            open
            onClose={() => setShowModal(false)}
            onComplete={handleModalComplete}
            data={contactWindowData}
          />
        )}

        {/* Follow-up Take Me Home modal */}
        {showTakeMeHome && (
          <PopupModalComponent
            open
            onClose={() => setShowTakeMeHome(false)}
            onComplete={handleTakeMeHomeComplete}
            data={TakeMeHome}
          />
        )}
      </Container>
    </>
  );
}

export default StandaloneCallback;
